import type { FormEvent} from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";

import { loginAndFetchUser, loginWithGoogle } from "@app/auth/api";
import { GoogleSignInButton } from "@app/auth/GoogleSignInButton";
import { useAuthStore } from "@app/auth/store";
import { extractErrorMessage } from "@app/api/client";

export default function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { token, user } = await loginAndFetchUser(email.trim(), password);
      setSession(token, user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleCredential = async (idToken: string): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const { token, user } = await loginWithGoogle(idToken);
      setSession(token, user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err, "Google sign-in failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Card sx={{ width: 380, maxWidth: "100%" }}>
        <CardContent component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h5">Backoffice</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in with an authorized admin account.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />
          <Button type="submit" variant="contained" size="large" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
            <>
              <Divider sx={{ color: "text.secondary", fontSize: 13 }}>or</Divider>
              <GoogleSignInButton onCredential={onGoogleCredential} />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
